<script>
	import { pageNumber, model } from "../../stores";
	import PageSection from "../../components/PageSection.svelte";
	import NavigationButtons from "../../components/NavigationButtons.svelte";

	import FormRow from "../../components/FormRow.svelte";
	import FormColumn from "../../components/FormColumn.svelte";

	import Select from "../../components/Select.svelte";
	import TextArea from "../../components/TextArea.svelte";
	import Datepicker from "../../components/Datepicker.svelte";
	import YesNoGroup from "../../components/YesNoGroup.svelte";
	import CheckboxList from "../../components/CheckboxList.svelte";
	import Input from "../../components/Input.svelte";

	import { yesNoOptions, disruptors, disruptionRatingOptions } from "../../config";
	import registrationTypes from "../../config/registrationTypes";

	export let pageIndex = null;
	export let nextPage = null;
	export let previousPage = null;
	export let persistUrl = null;
	export let pageTitle = null;
	export let disabled = false;
	export let lastVisitedPage = null;
	export let title = null;

	let required = true;

	// component state updating
	if (!disabled) {
		pageNumber.update((p) => pageIndex);
		$model.LastPageVisited = lastVisitedPage;
		document.title = pageTitle;
	}

	const onChangeUnwellBeforeOnboard = (e) => {
		$model.UnwellDetails = e.target.value === "Yes" ? $model.UnwellDetails : null;
	};

	const onChangeContractedOnboard = (e) => {
		$model.DateFirstSymptoms = e.target.value === "Yes" ? $model.DateFirstSymptoms : null;
		$model.DateSymptomsResolved = e.target.value === "Yes" ? $model.DateSymptomsResolved : null;
	};

	const onChangeTravel = (e) => {
		$model.ContractedPassengerName = null;
	};

	let questionLabel = "";
	let questionLabel2 = "";
	$: questionLabel =
		$model.RegistrationType && $model.RegistrationType === registrationTypes.types.Myself.value ? "you" : "they";
	$: questionLabel2 =
		$model.RegistrationType && $model.RegistrationType === registrationTypes.types.Myself.value ? "your" : "their";
</script>

<PageSection>
	<h2>Claim Details</h2>
	<FormRow>
		<FormColumn>
			<label for="unwellBeforeOnboard" class:isrequired={required}
				>Were {questionLabel} unwell before boarding the Sun Princess?</label
			>
			<YesNoGroup
				name="unwellBeforeOnboard"
				bind:value={$model.UnwellBeforeOnboard}
				optionList={yesNoOptions}
				{required}
				{disabled}
				on:change={onChangeUnwellBeforeOnboard}
			></YesNoGroup>
		</FormColumn>
	</FormRow>

	{#if $model.UnwellBeforeOnboard === "Yes"}
		<FormRow>
			<FormColumn cols="6">
				<TextArea
					{disabled}
					{required}
					label="If yes, please provide further detail below"
					id="unwellDetails"
					name="unwellDetails"
					bind:value={$model.UnwellDetails}
					autoExpand={true}
				/>
			</FormColumn>
		</FormRow>
	{/if}

	<FormRow>
		<FormColumn>
			<label for="contractedOnboard" class:isrequired={required}
				>Did {questionLabel} contract norovirus while on board Sun Princess?</label
			>
			<label
				><i
					>Symptoms of norovirus include an upset stomach or intestinal inflammation, often causing diarrhea,
					vomiting, abdominal pain or nausea.</i
				></label
			>
			<YesNoGroup
				name="contractedOnboard"
				bind:value={$model.ContractedOnboard}
				optionList={yesNoOptions}
				{required}
				{disabled}
				on:change={onChangeContractedOnboard}
			></YesNoGroup>
		</FormColumn>
	</FormRow>

	{#if $model.ContractedOnboard === "Yes"}
		<FormRow>
			<FormColumn>
				<label for="dateFirstSymptoms" class:isrequired={required}>Date of first symptoms</label>
			</FormColumn>
			<FormColumn cols="6">
				<Datepicker
					{required}
					{disabled}
					id="dateFirstSymptoms"
					bind:formattedSelected={$model.DateFirstSymptoms}
					start={new Date(2000, 0, 1)}
					end={new Date()}
					placeholder="e.g. 01/05/2020"
					openOnClass="calendar-pop"
					validator="true"
				/>
			</FormColumn>
		</FormRow>

		<FormRow>
			<FormColumn>
				<label for="dateSymptomsResolved" class:isrequired={required}>Date symptoms resolved</label>
			</FormColumn>
			<FormColumn cols="6">
				<Datepicker
					{required}
					{disabled}
					id="dateSymptomsResolved"
					bind:formattedSelected={$model.DateSymptomsResolved}
					start={new Date(2000, 0, 1)}
					end={new Date()}
					placeholder="e.g. 01/05/2020"
					openOnClass="calendar-pop"
					validator="true"
				/>
			</FormColumn>
		</FormRow>
	{/if}

	<FormRow>
		<FormColumn cols="6">
			<YesNoGroup
				name="travelWithContractedPassenger"
				label="Were {questionLabel} travelling with a passenger who contracted norovirus?"
				bind:value={$model.TravelWithContractedPassenger}
				optionList={yesNoOptions}
				{required}
				{disabled}
				on:change={onChangeTravel}
			></YesNoGroup>
		</FormColumn>

		{#if $model.TravelWithContractedPassenger === "Yes"}
			<FormColumn cols="6">
				<Input
					id="contractedPassengerName"
					{disabled}
					required={true}
					maxlength="40"
					label="What is their name"
					bind:value={$model.ContractedPassengerName}
				/>
			</FormColumn>
		{/if}
	</FormRow>

	<FormRow>
		<FormColumn>
			<label for="feelingEnjoymentDisrupted" class:isrequired={required}
				>Do {questionLabel} feel that {questionLabel2} enjoyment of the cruise was disrupted or diminished due to
				the norovirus outbreak?</label
			>
			<YesNoGroup
				name="feelingEnjoymentDisrupted"
				bind:value={$model.FeelingEnjoymentDisrupted}
				optionList={yesNoOptions}
				{required}
				{disabled}
			></YesNoGroup>
		</FormColumn>
	</FormRow>

	{#if $model.FeelingEnjoymentDisrupted === "Yes"}
		<FormRow>
			<FormColumn>
				<label for="enjoymentDisrupted" class:isrequired={required}
					>How was {questionLabel2} enjoyment of the cruise disrupted?</label
				>
			</FormColumn>
			<CheckboxList
				validator="checkboxRequired"
				validatorValue="1"
				validatorMessage="At least one item must be selected"
				validatorTarget=".checkbox-error"
				{disabled}
				{required}
				name="enjoymentDisrupted"
				options={disruptors}
				bind:selectedOptions={$model.Disruptors}
				useOther={true}
				maxLengthOther="1000"
				otherOptionValue="Other"
				otherLabel="If Other, please specify"
				otherInputType="textarea"
			></CheckboxList>
		</FormRow>

		<FormRow>
			<FormColumn>
				<label for="disruptionRating" class:isrequired={required}
					>Please rate the disruption on a scale from 1 to 5</label
				><br />
				<label><i>1 being mildly disruptive and 5 being severely disruptive</i></label>
			</FormColumn>
			<FormColumn cols="6">
				<Select
					{disabled}
					{required}
					bind:value={$model.DisruptionRating}
					optionList={disruptionRatingOptions}
					id="disruptionRating"
				></Select>
			</FormColumn>
		</FormRow>
	{/if}

	<h2>Further Investigations</h2>
	<FormRow>
		<FormColumn>
			<label for="confidentialStatement" class:isrequired={required}
				>Are {questionLabel} willing to provide a confidential statement about {questionLabel2} experience?</label
			>
			<YesNoGroup
				name="confidentialStatement"
				bind:value={$model.ConfidentialStatement}
				optionList={yesNoOptions}
				{required}
				{disabled}
			></YesNoGroup>
		</FormColumn>
	</FormRow>

	<FormRow>
		<FormColumn>
			<label for="shareExperienceToMedia"
				>Are {questionLabel} willing to do media to share {questionLabel2} experience?</label
			>
			<YesNoGroup
				name="shareExperienceToMedia"
				bind:value={$model.ShareExperienceToMedia}
				optionList={yesNoOptions}
				{disabled}
			></YesNoGroup>
		</FormColumn>
	</FormRow>

	{#if !disabled}
		<div class="form-group form-row">
			<div class="col-md-12 last">
				<NavigationButtons {persistUrl} {previousPage} {nextPage} pageName={title} tealiumTag={true} />
			</div>
		</div>
	{/if}
</PageSection>
